import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { DayOfWeek } from '../../types';

export interface FilterPanelProps {
  availableFilters: {
    days?: DayOfWeek[];
    periods?: number[];
    teachers?: { id: string; name: string }[];
    yearGroups?: { id: string; name: string }[];
    rooms?: { id: string; name: string }[];
    subjects?: { id: string; name: string }[];
    weeks?: { id: string; name: string }[];
  };
  activeFilters: {
    day?: DayOfWeek;
    period?: number;
    teacherId?: string;
    yearGroupId?: string;
    roomId?: string;
    subjectId?: string;
    week?: string;
  };
  onFilterChange: (filters: {
    day?: DayOfWeek;
    period?: number;
    teacherId?: string;
    yearGroupId?: string;
    roomId?: string;
    subjectId?: string;
    week?: string;
  }) => void;
  onClearAll?: () => void;
  showAdvanced?: boolean;
  compact?: boolean;
}

export const FilterPanel: React.FC<FilterPanelProps> = ({
  availableFilters,
  activeFilters,
  onFilterChange,
  onClearAll,
  showAdvanced = false,
  compact = false,
}) => {
  const [localFilters, setLocalFilters] = useState(activeFilters);
  const [advancedOpen, setAdvancedOpen] = useState(showAdvanced);
  const [searchText, setSearchText] = useState('');

  const handleFilterChange = (filterKey: keyof typeof activeFilters, value: any) => {
    const newFilters = {
      ...localFilters,
      [filterKey]: value || undefined,
    };
    setLocalFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleClearAll = () => {
    setLocalFilters({});
    onFilterChange({});
    onClearAll?.();
  };

  const getActiveFilterCount = () => {
    return Object.values(localFilters).filter((v) => v !== undefined).length;
  };

  const dayLabels: Record<DayOfWeek, string> = {
    monday: 'Monday',
    tuesday: 'Tuesday',
    wednesday: 'Wednesday',
    thursday: 'Thursday',
    friday: 'Friday',
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterListIcon color="primary" />
          <Typography variant="h6">Filters</Typography>
          {getActiveFilterCount() > 0 && (
            <Chip label={`${getActiveFilterCount()} active`} size="small" color="primary" variant="outlined" />
          )}
        </Box>
        {getActiveFilterCount() > 0 && (
          <Button startIcon={<ClearIcon />} onClick={handleClearAll} size="small" color="error">
            Clear All
          </Button>
        )}
      </Box>

      <Divider sx={{ mb: 2 }} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {availableFilters.days && (
          <FormControl fullWidth size="small">
            <InputLabel>Day</InputLabel>
            <Select
              value={localFilters.day || ''}
              label="Day"
              onChange={(e: SelectChangeEvent) => handleFilterChange('day', e.target.value as DayOfWeek)}
            >
              <MenuItem value="">
                <em>All Days</em>
              </MenuItem>
              {availableFilters.days.map((day) => (
                <MenuItem key={day} value={day}>
                  {dayLabels[day]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {availableFilters.periods && (
          <FormControl fullWidth size="small">
            <InputLabel>Period</InputLabel>
            <Select
              value={localFilters.period?.toString() || ''}
              label="Period"
              onChange={(e: SelectChangeEvent) =>
                handleFilterChange('period', e.target.value ? Number.parseInt(e.target.value) : undefined)
              }
            >
              <MenuItem value="">
                <em>All Periods</em>
              </MenuItem>
              {availableFilters.periods.map((period) => (
                <MenuItem key={period} value={period.toString()}>
                  Period {period}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {availableFilters.teachers && (
          <FormControl fullWidth size="small">
            <InputLabel>Teacher</InputLabel>
            <Select
              value={localFilters.teacherId || ''}
              label="Teacher"
              onChange={(e: SelectChangeEvent) => handleFilterChange('teacherId', e.target.value)}
            >
              <MenuItem value="">
                <em>All Teachers</em>
              </MenuItem>
              {availableFilters.teachers
                .filter((t) => !searchText || t.name.toLowerCase().includes(searchText.toLowerCase()))
                .map((teacher) => (
                  <MenuItem key={teacher.id} value={teacher.id}>
                    {teacher.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        )}

        {availableFilters.yearGroups && (
          <FormControl fullWidth size="small">
            <InputLabel>Year Group</InputLabel>
            <Select
              value={localFilters.yearGroupId || ''}
              label="Year Group"
              onChange={(e: SelectChangeEvent) => handleFilterChange('yearGroupId', e.target.value)}
            >
              <MenuItem value="">
                <em>All Year Groups</em>
              </MenuItem>
              {availableFilters.yearGroups.map((year) => (
                <MenuItem key={year.id} value={year.id}>
                  {year.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {availableFilters.rooms && (
          <FormControl fullWidth size="small">
            <InputLabel>Room</InputLabel>
            <Select
              value={localFilters.roomId || ''}
              label="Room"
              onChange={(e: SelectChangeEvent) => handleFilterChange('roomId', e.target.value)}
            >
              <MenuItem value="">
                <em>All Rooms</em>
              </MenuItem>
              {availableFilters.rooms
                .filter((r) => !searchText || r.name.toLowerCase().includes(searchText.toLowerCase()))
                .map((room) => (
                  <MenuItem key={room.id} value={room.id}>
                    {room.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        )}

        {availableFilters.subjects && (
          <FormControl fullWidth size="small">
            <InputLabel>Subject</InputLabel>
            <Select
              value={localFilters.subjectId || ''}
              label="Subject"
              onChange={(e: SelectChangeEvent) => handleFilterChange('subjectId', e.target.value)}
            >
              <MenuItem value="">
                <em>All Subjects</em>
              </MenuItem>
              {availableFilters.subjects
                .filter((s) => !searchText || s.name.toLowerCase().includes(searchText.toLowerCase()))
                .map((subject) => (
                  <MenuItem key={subject.id} value={subject.id}>
                    {subject.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        )}

        {availableFilters.weeks && (
          <FormControl fullWidth size="small">
            <InputLabel>Week</InputLabel>
            <Select
              value={localFilters.week || 'all'}
              label="Week"
              onChange={(e: SelectChangeEvent) => handleFilterChange('week', e.target.value)}
            >
              {availableFilters.weeks.map((week) => (
                <MenuItem key={week.id} value={week.id}>
                  {week.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Advanced Filters Toggle */}
        {!compact && (
          <>
            <Divider sx={{ my: 1 }} />
            <FormControlLabel
              control={<Switch checked={advancedOpen} onChange={(e) => setAdvancedOpen(e.target.checked)} size="small" />}
              label={<Typography variant="body2">Advanced Filters</Typography>}
            />

            <Collapse in={advancedOpen}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <TextField
                  size="small"
                  label="Search"
                  placeholder="Search teachers, rooms..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  fullWidth
                />
              </Box>
            </Collapse>
          </>
        )}
      </Box>
    </Paper>
  );
};

export default FilterPanel;
